import { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { getStoredSession } from '@estream/sdk-browser/react';

// Context
import { DebugProvider, useDebug, setGlobalDebugEmitter } from './context/DebugContext';
import { SettingsProvider } from './context/SettingsContext';

// Layout and Debug
import Layout from './components/Layout';
import DebugPanel from './components/DebugPanel';

// Pages
import Dashboard from './pages/Dashboard';
import Topology from './pages/Topology';
import Circuits from './pages/Circuits';
import Governance from './pages/Governance';
import Settings from './pages/Settings';
import Login from './pages/Login';
import Lattices from './pages/Lattices';
import Identity from './pages/Identity';
import Bridge from './pages/Bridge';
import LoadSim from './pages/LoadSim';
import Messages from './pages/Messages';
import Infrastructure from './pages/Infrastructure';
import Releases from './pages/Releases';
import GenesisWizard from './pages/GenesisWizard';
import Tenants from './pages/Tenants';
import Fees from './pages/Fees';
import Observability from './pages/Observability';
import CircuitDebugger from './pages/CircuitDebugger';
import ApiDocs from './pages/ApiDocs';
import Billing from './pages/Billing';
import Policies from './pages/Policies';
import AuditLog from './pages/AuditLog';
import Team from './pages/Team';

// Genesis state type
interface GenesisStatus {
  initialized: boolean;
  status: 'genesis_required' | 'active' | 'error';
  network_id?: string;
  error?: string;
}

// Debug emitter registration
function DebugEmitterRegistration() {
  const { addEvent } = useDebug();
  
  useEffect(() => {
    setGlobalDebugEmitter(addEvent);
    return () => setGlobalDebugEmitter(null);
  }, [addEvent]);
  
  return null;
}

function AppRoutes() {
  const location = useLocation();
  const { addEvent } = useDebug();
  const [previousPath, setPreviousPath] = useState<string | null>(null);
  
  // Emit navigation events on route changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    addEvent({
      source: 'console',
      category: 'navigation',
      message: previousPath ? `${previousPath} → ${currentPath}` : `Navigate to ${currentPath}`,
      data: { 
        path: currentPath, 
        from: previousPath,
        search: location.search,
        hash: location.hash,
      },
    });
    
    setPreviousPath(currentPath);
  }, [location.pathname, addEvent]); // Only trigger on pathname changes
  
  // Genesis state - check if network is initialized
  const [genesisStatus, setGenesisStatus] = useState<GenesisStatus | null>(null);
  const [genesisLoading, setGenesisLoading] = useState(true);
  
  // Check genesis status on mount
  useEffect(() => {
    const checkGenesis = async () => {
      try {
        const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
        const response = await fetch(`${edgeUrl}/api/genesis/status`);
        if (response.ok) {
          const status = await response.json();
          setGenesisStatus(status);
          console.log('[App] Genesis status:', status);
        } else {
          // Assume genesis required if endpoint fails
          setGenesisStatus({ initialized: false, status: 'genesis_required' });
        }
      } catch (error) {
        console.warn('[App] Genesis check failed, assuming initialized:', error);
        // On network error, assume initialized to not block development
        setGenesisStatus({ initialized: true, status: 'active' });
      } finally {
        setGenesisLoading(false);
      }
    };
    
    checkGenesis();
  }, []);
  
  // Re-check session on every route change to pick up login/logout
  const checkSession = useCallback(() => {
    const session = getStoredSession();
    const rawSession = typeof sessionStorage !== 'undefined' 
      ? sessionStorage.getItem('estream_session') 
      : null;
    console.log('[App] Session check:', {
      hasSession: session !== null,
      rawExists: rawSession !== null,
      rawLength: rawSession?.length,
      path: location.pathname,
    });
    return session !== null;
  }, [location.pathname]);
  
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const initial = checkSession();
    console.log('[App] Initial login state:', initial);
    return initial;
  });
  
  // Re-check session when location changes (after login redirect)
  useEffect(() => {
    const newState = checkSession();
    console.log('[App] Route changed, updating login state:', newState);
    setIsLoggedIn(newState);
  }, [location, checkSession]);
  
  // Show loading while checking genesis
  if (genesisLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: 'var(--bg-page)',
        color: 'var(--text-secondary)',
      }}>
        <div className="spinner spinner-lg" />
      </div>
    );
  }
  
  // If genesis not complete and not on genesis page, redirect
  const needsGenesis = genesisStatus && !genesisStatus.initialized;

  // Protected route wrapper
  function ProtectedRoute({ children }: { children: React.ReactNode }) {
    if (!isLoggedIn) {
      console.debug('[App] Not logged in, redirecting to /login');
      return <Navigate to="/login" replace />;
    }
    return <>{children}</>;
  }

  return (
    <>
      <DebugEmitterRegistration />
      <Routes>
        {/* Public route */}
        <Route path="/login" element={<Login />} />
        
        {/* Genesis Wizard - shown when network needs initialization */}
        <Route 
          path="/genesis" 
          element={
            <ProtectedRoute>
              <GenesisWizard />
            </ProtectedRoute>
          } 
        />
        
        {/* Protected routes - only accessible after genesis */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              {needsGenesis ? <Navigate to="/genesis" replace /> : <Layout />}
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="topology" element={<Topology />} />
          <Route path="circuits" element={<Circuits />} />
          
          {/* Governance routes - Central Hub (#259) */}
          <Route path="governance" element={<Governance />} />
          <Route path="governance/activity" element={<AuditLog />} />
          <Route path="governance/key-holders" element={<Identity />} />
          <Route path="governance/policies" element={<Policies />} />
          
          {/* Legacy routes - redirect to new structure */}
          <Route path="governance/approvals" element={<Navigate to="/governance" replace />} />
          <Route path="identity" element={<Navigate to="/governance/key-holders" replace />} />
          <Route path="approvals" element={<Navigate to="/governance" replace />} />
          
          <Route path="lattices" element={<Lattices />} />
          <Route path="lattices/nodes" element={<Lattices />} />
          <Route path="lattices/circuits" element={<Lattices />} />
          <Route path="bridge" element={<Bridge />} />
          <Route path="infrastructure" element={<Infrastructure />} />
          <Route path="releases" element={<Releases />} />
          <Route path="loadsim" element={<LoadSim />} />
          <Route path="messages" element={<Messages />} />
          <Route path="team" element={<Team />} />
          {/* Tenant routes (#259) */}
          <Route path="tenant" element={<Tenants />} />
          <Route path="tenant/subscription" element={<Tenants />} /> {/* TODO: Create dedicated Subscription page */}
          <Route path="fees" element={<Fees />} />
          <Route path="observability" element={<Observability />} />
          <Route path="circuits/debug" element={<CircuitDebugger />} />
          <Route path="circuits/debug/:circuitId" element={<CircuitDebugger />} />
          <Route path="docs" element={<ApiDocs />} />
          <Route path="billing" element={<Billing />} />
          <Route path="settings" element={<Settings />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <DebugPanel />
    </>
  );
}

export default function App() {
  return (
    <SettingsProvider>
      <DebugProvider>
        <AppRoutes />
      </DebugProvider>
    </SettingsProvider>
  );
}
